import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";
import CyberEssentialsCostCalculator from "@/components/CyberEssentialsCostCalculator";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { 
  Shield, 
  CheckCircle, 
  ArrowRight,
  Phone,
  Clock,
  Award,
  Users
} from "lucide-react";

const CyberEssentialsCalculator = () => {
  const schemaMarkup = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "WebApplication",
        "@id": "https://4fox.uk/cyber-essentials-calculator#app",
        "name": "Cyber Essentials Cost Calculator UK",
        "description": "Free online tool to calculate your Cyber Essentials certification cost based on business size, security posture, and certification level needed.",
        "applicationCategory": "BusinessApplication",
        "operatingSystem": "Web",
        "url": "https://4fox.uk/cyber-essentials-calculator",
        "offers": {
          "@type": "Offer",
          "price": "0",
          "priceCurrency": "GBP",
          "description": "Free cost estimation tool for Cyber Essentials certification"
        },
        "provider": {
          "@type": "Organization",
          "name": "4FOX Security",
          "url": "https://4fox.uk"
        }
      },
      {
        "@type": "BreadcrumbList",
        "@id": "https://4fox.uk/cyber-essentials-calculator#breadcrumb",
        "itemListElement": [
          {
            "@type": "ListItem",
            "position": 1,
            "name": "Home",
            "item": "https://4fox.uk"
          },
          {
            "@type": "ListItem",
            "position": 2,
            "name": "Cyber Essentials UK",
            "item": "https://4fox.uk/cyber-essentials-uk"
          },
          {
            "@type": "ListItem",
            "position": 3,
            "name": "Cost Calculator",
            "item": "https://4fox.uk/cyber-essentials-calculator"
          }
        ]
      },
      {
        "@type": "WebPage",
        "@id": "https://4fox.uk/cyber-essentials-calculator#webpage",
        "url": "https://4fox.uk/cyber-essentials-calculator",
        "name": "Cyber Essentials Cost Calculator UK | Get Instant Pricing | 4FOX Security",
        "description": "Calculate your Cyber Essentials certification cost instantly. Free UK pricing tool based on business size, security posture, and certification level needed.",
        "isPartOf": {
          "@id": "https://4fox.uk/#website"
        },
        "breadcrumb": {
          "@id": "https://4fox.uk/cyber-essentials-calculator#breadcrumb"
        },
        "inLanguage": "en-GB"
      }
    ]
  };

  return (
    <div className="min-h-screen bg-background">
      <SEO
        title="Cyber Essentials Cost Calculator UK | Get Instant Pricing | 4FOX Security"
        description="Calculate your Cyber Essentials certification cost instantly. Free UK pricing tool based on business size, security posture, and certification level needed. Get certified from £495."
        canonical="https://4fox.uk/cyber-essentials-calculator"
        keywords={[
          "Cyber Essentials cost calculator",
          "Cyber Essentials price UK",
          "how much does Cyber Essentials cost",
          "Cyber Essentials certification cost",
          "Cyber Essentials Plus pricing",
          "CE certification price",
          "Cyber Essentials quote",
          "UK cyber certification cost"
        ]}
        ogTitle="Cyber Essentials Cost Calculator | Free Instant Pricing Tool"
        ogDescription="Calculate your Cyber Essentials certification cost in seconds. Free UK pricing tool for businesses of all sizes."
        ogType="website"
        ogUrl="https://4fox.uk/cyber-essentials-calculator"
        schema={schemaMarkup}
        hreflang="en-gb"
        region="GB"
      />

      <Navbar />

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-primary via-primary/95 to-primary/90 text-white py-16 lg:py-20 overflow-hidden">
        <div className="absolute inset-0 bg-[url('/hero-cyber-optimized.webp')] bg-cover bg-center opacity-10" />
        <div className="absolute inset-0 bg-gradient-to-r from-primary/90 to-primary/70" />

        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-3xl mx-auto text-center">
            <Badge className="bg-white/20 text-white border-white/30 mb-6 text-sm px-4 py-1">
              Free Instant Pricing Tool
            </Badge>

            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6 leading-tight">
              Cyber Essentials Cost Calculator
            </h1>

            <p className="text-lg md:text-xl text-white/90 mb-8 max-w-2xl mx-auto">
              Get an instant estimate for your Cyber Essentials certification. Answer 4 quick questions to see your pricing and timeline.
            </p>

            {/* Trust Signals */}
            <div className="flex flex-wrap justify-center gap-4 mb-8">
              <div className="flex items-center gap-2 bg-white/10 rounded-full px-3 py-1.5 text-sm">
                <Award className="h-4 w-4 text-accent" />
                <span>98% Pass Rate</span>
              </div>
              <div className="flex items-center gap-2 bg-white/10 rounded-full px-3 py-1.5 text-sm">
                <Clock className="h-4 w-4 text-accent" />
                <span>Takes 30 Seconds</span>
              </div>
              <div className="flex items-center gap-2 bg-white/10 rounded-full px-3 py-1.5 text-sm">
                <Shield className="h-4 w-4 text-accent" />
                <span>No Obligation</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Calculator Section */}
      <section className="py-12 lg:py-16 bg-muted/30">
        <div className="container mx-auto px-4">
          <CyberEssentialsCostCalculator />
        </div>
      </section>

      {/* Why Use Our Calculator */}
      <section className="py-12 lg:py-16 bg-background">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-bold text-center mb-8">
              Why Use Our Cost Calculator?
            </h2>

            <div className="grid md:grid-cols-3 gap-6">
              <div className="text-center p-6">
                <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Clock className="h-6 w-6 text-primary" />
                </div>
                <h3 className="font-semibold mb-2">Instant Results</h3>
                <p className="text-sm text-muted-foreground">
                  Get your estimate in seconds, not days. No waiting for callbacks or emails.
                </p>
              </div>

              <div className="text-center p-6">
                <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="h-6 w-6 text-primary" />
                </div>
                <h3 className="font-semibold mb-2">Accurate Estimates</h3>
                <p className="text-sm text-muted-foreground">
                  Based on real pricing for over 100+ UK business certifications.
                </p>
              </div>

              <div className="text-center p-6">
                <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Shield className="h-6 w-6 text-primary" />
                </div>
                <h3 className="font-semibold mb-2">No Obligation</h3>
                <p className="text-sm text-muted-foreground">
                  No email required, no pushy sales calls. Just honest pricing.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Overview */}
      <section className="py-12 lg:py-16 bg-muted/30">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-bold text-center mb-4">
              Cyber Essentials Pricing Overview
            </h2>
            <p className="text-center text-muted-foreground mb-8 max-w-2xl mx-auto">
              Transparent pricing for UK businesses. All packages include expert guidance and first-time pass support.
            </p>

            <div className="overflow-x-auto">
              <table className="w-full border-collapse bg-background rounded-lg overflow-hidden shadow-sm">
                <thead>
                  <tr className="bg-primary text-primary-foreground">
                    <th className="p-4 text-left font-semibold">Business Size</th>
                    <th className="p-4 text-left font-semibold">Cyber Essentials</th>
                    <th className="p-4 text-left font-semibold">Cyber Essentials Plus</th>
                    <th className="p-4 text-left font-semibold">Timeline</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-border">
                    <td className="p-4 font-medium">1-9 employees</td>
                    <td className="p-4">From £495</td>
                    <td className="p-4">From £1,995</td>
                    <td className="p-4">1-3 weeks</td>
                  </tr>
                  <tr className="border-b border-border bg-muted/30">
                    <td className="p-4 font-medium">10-49 employees</td>
                    <td className="p-4">From £995</td>
                    <td className="p-4">From £2,495</td>
                    <td className="p-4">3-5 weeks</td>
                  </tr>
                  <tr className="border-b border-border">
                    <td className="p-4 font-medium">50-249 employees</td>
                    <td className="p-4">From £1,495</td>
                    <td className="p-4">From £3,495</td>
                    <td className="p-4">4-6 weeks</td>
                  </tr>
                  <tr className="bg-muted/30">
                    <td className="p-4 font-medium">250+ employees</td>
                    <td className="p-4" colSpan={2}>Custom Quote</td>
                    <td className="p-4">6-10 weeks</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <p className="text-center text-sm text-muted-foreground mt-4">
              Prices exclude VAT. Final pricing depends on security posture and IT complexity.
            </p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-12 lg:py-16 bg-primary text-primary-foreground">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-2xl md:text-3xl font-bold mb-4">
              Ready to Get Certified?
            </h2>
            <p className="text-lg text-primary-foreground/80 mb-8">
              Start your Cyber Essentials journey today. Our UK-based consultants are ready to help.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                asChild
                size="lg"
                className="bg-accent hover:bg-accent/90 text-accent-foreground"
              >
                <Link to="/services/cyber-essentials-micro">
                  Get Started Now
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Link>
              </Button>
              <Button
                asChild
                size="lg"
                variant="outline"
                className="border-white text-white hover:bg-white/10"
              >
                <Link to="/contact">
                  <Phone className="mr-2 h-5 w-5" />
                  Speak to an Expert
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default CyberEssentialsCalculator;
